/** Графа «Сверка с листами РД» и подсказки к признакам отбора (выпуск 1б плана
 *  по замечаниям ПТО, №4 п. 6): «✓ л. 6 поз. 2 (4 шт)» у строки, которая учла
 *  строку листа РД, — по обратному индексу `counted_as` с бэкенда, только при
 *  равных количестве и единице. С выпуска 2 графа — текст бэкенда, как в Excel
 *  (`sheet_check`): «✓» и «⚠» по связям, мягкому досвязыванию и ВОР, пометки
 *  строк («нет в СП01 — проверьте»); отметка учтённой строки — ссылка на её
 *  лист. У строки «Есть в ВОР, нет в СП» — кандидат пары из СП.
 *
 *  Без импортов значений (только `import type`): модуль проверяет `node --test`
 *  (`volumes/tabs.test.mjs`). */
import type { PtoVolumeMark, PtoVolumePosition, PtoVolumeSheetCheck } from '@shared/api';

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null;
}

function text(v: unknown): string | null {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** Поля строки для подписи места и отметки сверки. */
export type CheckRow = Pick<
  PtoVolumePosition,
  'id' | 'code' | 'name' | 'unit' | 'quantity' | 'page_number' | 'raw'
>;

/** Код части и номер листа с основной надписи (`raw.sheet`), страница PDF. */
function place(p: Pick<PtoVolumePosition, 'page_number' | 'raw'>) {
  const sheet = asObj(asObj(p.raw)?.sheet);
  const code = text(sheet?.code);
  const no = text(sheet?.no);
  const sheetPage = typeof sheet?.page === 'number' ? sheet.page : 0;
  const page = p.page_number > 0 ? p.page_number : sheetPage;
  return { at: code && no ? `${code} л. ${no}` : null, pdf: page > 0 ? `стр. PDF ${page}` : null };
}

/** Коротко, для отметки: «ЧТЖ01 л. 2», без основной надписи — «стр. PDF 6». */
export function shortPlace(p: Pick<PtoVolumePosition, 'page_number' | 'raw'>): string {
  const { at, pdf } = place(p);
  return at ?? pdf ?? '';
}

/** Полностью, для подсказки: «ЧТЖ01 л. 2 (стр. PDF 6)» или «стр. PDF 6». */
export function fullPlace(p: Pick<PtoVolumePosition, 'page_number' | 'raw'>): string {
  const { at, pdf } = place(p);
  if (at && pdf) return `${at} (${pdf})`;
  return at ?? pdf ?? '';
}

/** Количество с единицей: «4 шт», «120 м»; без количества — «—». */
export function qtyText(q: number | null | undefined, unit: string | null | undefined): string {
  if (q === null || q === undefined || !Number.isFinite(q)) return '—';
  const n = q.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
  const u = unit?.trim();
  return u ? `${n} ${u}` : n;
}

/** «Поз.» строки на листе (графа «Поз.» / «Марка» разбора — `code`), если она
 *  короткая: длинное значение — обозначение, а не позиция. */
export function posText(code: string | null | undefined): string | null {
  const c = code?.trim();
  return c && c.length <= 12 ? c : null;
}

/** Учтённые строки с «✓»: равные количество и единица. */
export function confirmedChecks(mark: PtoVolumeMark | undefined): PtoVolumeSheetCheck[] {
  return (mark?.checks ?? []).filter((c) => c.same_qty && c.same_unit);
}

/** Отметка «✓ л. 6 поз. 2 (4 шт)» по учтённой строке листа РД. */
export function checkLabel(row: CheckRow): string {
  const pos = posText(row.code);
  const at = shortPlace(row);
  return `✓ ${[at, pos ? `поз. ${pos}` : null].filter(Boolean).join(' ')} (${qtyText(row.quantity, row.unit)})`;
}

/** Подсказка к отметке: где строка, её наименование и число. */
export function checkTitle(row: CheckRow): string {
  const pos = posText(row.code);
  const at = [fullPlace(row), pos ? `поз. ${pos}` : null].filter(Boolean).join(', ');
  const name = row.name.replace(/\s+/g, ' ').trim();
  return `Строка листа РД ${at}: «${name}» — ${qtyText(row.quantity, row.unit)}; то же количество и единица. Открыть лист`;
}

/** Подпись признака отбора для графы «Статус»: «в сумме», «повтор», «вне
 *  суммы»; признаков нет — null. */
export function sumBadge(
  mark: PtoVolumeMark | undefined,
): 'в сумме' | 'повтор' | 'вне суммы' | null {
  if (!mark) return null;
  if (mark.in_sum) return 'в сумме';
  return mark.repeat ? 'повтор' : 'вне суммы';
}

/** Подсказка к признаку: причина исключения и строка, которая учла число
 *  (`counted` — её подпись: «СП01 №5 «Реостат…» (СП01 л. 1, стр. PDF 7)»). */
export function sumHint(
  mark: PtoVolumeMark | undefined,
  counted: string | null,
): string | undefined {
  if (!mark) return undefined;
  if (mark.in_sum) {
    return mark.possible_dup
      ? `В сумме объёмов; возможный повтор строки сводной${counted ? ` ${counted}` : ''} — проверьте`
      : 'В сумме объёмов: строка входит в ведомость';
  }
  const why = mark.reason_label ?? mark.reason ?? 'причина не указана';
  const head = mark.repeat ? 'Повтор вне суммы' : 'Вне суммы';
  return `${head}: ${why}${counted ? `. Число учтено в строке ${counted}` : ''}`;
}

/** Вид отметки графы сверки: «✓», «⚠», пометка строки, «и ещё N». */
export type CheckTone = 'ok' | 'warn' | 'note' | 'more';

/** Отметка графы «Сверка с листами РД» по признакам выпуска 2. */
export interface CheckMark {
  text: string;
  tone: CheckTone;
  /** Учтённая строка, чья это отметка (ссылка на её лист); null — отметка без
   *  строки (пометка, «то же число» ВОР, «и ещё N»). */
  target: string | null;
}

const MORE_RE = /^и ещё \d+$/;

/** Отметки графы «Сверка с листами РД» из текста бэкенда (`sheet_check`, как в
 *  Excel: отметки через «; »). Отметка, совпавшая с `checks[].text`, ведёт на
 *  лист учтённой строки. Текста нет (выпуск 1б, флаг выключен) — пусто. */
export function sheetCheckMarks(mark: PtoVolumeMark | undefined): CheckMark[] {
  const text = mark?.sheet_check?.trim();
  if (!text) return [];
  const byText = new Map<string, string>();
  for (const c of mark?.checks ?? []) {
    if (c.text && !byText.has(c.text)) byText.set(c.text, c.id);
  }
  return text
    .split('; ')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => ({
      text: t,
      tone: t.startsWith('✓')
        ? 'ok'
        : t.startsWith('⚠')
          ? 'warn'
          : MORE_RE.test(t)
            ? 'more'
            : 'note',
      target: byText.get(t) ?? null,
    }));
}

/** Отметки учтённых строк, которых нет в тексте графы (за «и ещё N»), — для
 *  подсказки. */
export function hiddenCheckTexts(mark: PtoVolumeMark | undefined, shown: CheckMark[]): string[] {
  const seen = new Set(shown.map((m) => m.text));
  const out: string[] = [];
  for (const c of mark?.checks ?? []) {
    if (c.text && !seen.has(c.text)) {
      seen.add(c.text);
      out.push(c.text);
    }
  }
  return out;
}

/** Строка «Есть в ВОР, нет в СП»: кандидат пары из СП (как в Excel) и его
 *  позиция; не строка списка — null. */
export function vorCandidate(
  mark: PtoVolumeMark | undefined,
): { text: string; target: string | null } | null {
  if (!mark?.vor_not_in_spec) return null;
  const cand = mark.pair_candidate?.trim();
  return cand
    ? { text: `кандидат: ${cand}`, target: mark.pair_candidate_of ?? null }
    : { text: 'нет в СП', target: null };
}
